import { useState } from 'react'
import { useMe } from '../hooks/useMe'
import { useArchived } from '../contexts/ArchiveContext'
import {
  useEventBriefings,
  useBriefingTopics,
  useAddEventBriefings,
  useRemoveEventBriefing,
  useAckEventBriefing,
  useSendBriefingReminder
} from '../hooks/useBriefings'
import { ModalShell, CancelButton, PrimaryButton, ServerError } from './modal-form'
import { Icon } from './Icon'
import type { EventBriefingTopic } from '../types'

// BriefingsTab - per-event briefing pack with per-topic ack progress.
// Header: Add topics button + Send reminder button. Each topic renders as
// a card showing acked / rostered count, expandable body, and per-ack
// avatar grid with stale-version flagging.

interface Props {
  eventId: number
}

export function BriefingsTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: me } = useMe()
  const { data, isLoading, error } = useEventBriefings(eventId)
  const removeMut = useRemoveEventBriefing(eventId)
  const ackMut = useAckEventBriefing(eventId)
  const reminderMut = useSendBriefingReminder(eventId)

  const [showPicker, setShowPicker] = useState(false)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  if (isLoading) return <div className="text-ink-soft text-sm font-mono">Loading briefings…</div>
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load briefings</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }
  if (!data) return null

  const topics = data.topics
  const rostered = data.rostered
  const totalAcked = topics.reduce((s, t) => s + t.acks.filter((a) => !a.isStale).length, 0)
  const totalNeeded = topics.length * rostered.length

  function toggleExpand(topicId: number) {
    setExpanded((cur) => {
      const next = new Set(cur)
      if (next.has(topicId)) next.delete(topicId)
      else next.add(topicId)
      return next
    })
  }

  function sendReminders() {
    const ok = window.confirm(
      'Send a briefing reminder to all rostered users who haven\'t acknowledged?\n\n' +
      'Reminders skip anyone who received one in the last 24 hours.'
    )
    if (!ok) return
    reminderMut.mutate(null)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Briefings</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            Topics rostered staff and volunteers acknowledge before their shift.
            {topics.length > 0 && (
              <span className="ml-2 font-mono">
                {totalAcked} / {totalNeeded} acked across {topics.length} topic{topics.length === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
        {!archived && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={sendReminders}
              disabled={reminderMut.isPending || topics.length === 0 || rostered.length === 0}
              className="px-3 py-2 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green transition-colors disabled:opacity-50"
            >
              {reminderMut.isPending ? 'Sending…' : 'Send reminder'}
            </button>
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="px-3 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
            >
              + Add topics
            </button>
          </div>
        )}
      </div>

      {reminderMut.data && (
        <div
          className="rounded p-3 text-sm"
          style={{ background: '#dcebe5', color: '#0a4a3a' }}
        >
          Queued {reminderMut.data.queued} reminder{reminderMut.data.queued === 1 ? '' : 's'},
          skipped {reminderMut.data.skipped} (rate-limited).
          Real email delivery wires up once the SendCallbackEmail Function is
          generalised; intent is logged in dbo.CommsLog now.
        </div>
      )}

      {topics.length === 0 ? (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f5edd5', color: '#9b7f2a' }}
          >
            <Icon name="alert-triangle" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No briefing pack yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Build the briefing pack by picking topics from the org library.
            Rostered volunteers will see them on their pre-shift checklist
            once the volunteer surface ships.
          </p>
          {!archived && (
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
            >
              Add topics to pack
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {topics.map((t) => (
            <TopicCard
              key={t.id}
              topic={t}
              rosteredCount={rostered.length}
              isExpanded={expanded.has(t.id)}
              isAcked={!!me?.dbUserId && t.acks.some((a) => a.userId === me.dbUserId && !a.isStale)}
              canRemove={!!me?.isAdmin && !archived && !t.isOverview}
              canAck={!archived}
              ackBusy={ackMut.isPending && ackMut.variables === t.id}
              removeBusy={removeMut.isPending && removeMut.variables === t.id}
              onToggleExpand={() => toggleExpand(t.id)}
              onAck={() => ackMut.mutate(t.id)}
              onRemove={() => {
                const ok = window.confirm(`Remove "${t.title}" from this event's pack?`)
                if (ok) removeMut.mutate(t.id)
              }}
            />
          ))}
        </div>
      )}

      {!archived && showPicker && (
        <TopicPickerModal
          eventId={eventId}
          existingTopicIds={new Set(topics.map((t) => t.id))}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  )
}

// ---- Topic card -----------------------------------------------------------

function TopicCard({
  topic, rosteredCount, isExpanded, isAcked, canRemove, canAck, ackBusy, removeBusy,
  onToggleExpand, onAck, onRemove
}: {
  topic: EventBriefingTopic
  rosteredCount: number
  isExpanded: boolean
  isAcked: boolean
  canRemove: boolean
  canAck: boolean
  ackBusy: boolean
  removeBusy: boolean
  onToggleExpand: () => void
  onAck: () => void
  onRemove: () => void
}) {
  const validAcks = topic.acks.filter((a) => !a.isStale)
  const ackedCount = validAcks.length
  const pct = rosteredCount === 0 ? 0 : Math.min(100, Math.round((ackedCount / rosteredCount) * 100))
  const someStale = topic.acks.some((a) => a.isStale)

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 flex items-start gap-3">
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          className="flex items-center gap-2 flex-1 text-left"
        >
          <span aria-hidden className="text-ink-soft text-xs font-mono w-3">
            {isExpanded ? '▼' : '▶'}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-semibold text-sm">{topic.title}</span>
              {topic.isOverview && (
                <span
                  title="Generated automatically from the event details and kept up to date"
                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                  style={{ background: '#dcebe5', color: '#0a4a3a' }}
                >
                  Auto
                </span>
              )}
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide" style={{ background: '#e0e7ec', color: '#3d5a6c' }}>
                {topic.category}
              </span>
              <span className="text-xs text-ink-soft font-mono">
                ~{topic.durationMinutes} min · v{topic.version}
              </span>
              {someStale && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#f5edd5', color: '#9b7f2a' }}>
                  some acks stale
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 max-w-xs h-1.5 bg-parchment rounded overflow-hidden">
                <div className="h-full bg-green" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs text-ink-soft font-mono whitespace-nowrap">
                {ackedCount} / {rosteredCount} acked
              </span>
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isAcked && canAck && (
            <button
              type="button"
              onClick={onAck}
              disabled={ackBusy}
              className="text-xs font-semibold text-green hover:underline disabled:opacity-50"
            >
              {ackBusy ? 'Acking…' : 'Ack'}
            </button>
          )}
          {isAcked && (
            <span className="text-xs text-green font-mono">✓ Acked</span>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              disabled={removeBusy}
              aria-label="Remove from pack"
              title="Remove from pack"
              className="text-ink-soft hover:text-oxblood text-lg disabled:opacity-50"
            >
              ×
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="px-4 py-3 border-t border-line">
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{topic.bodyMarkdown}</div>
          {topic.acks.length > 0 && (
            <div className="mt-3 pt-3 border-t border-line">
              <div className="text-[10px] uppercase tracking-wide text-ink-soft font-semibold mb-2">
                Acknowledgements
              </div>
              <div className="flex flex-wrap gap-1.5">
                {topic.acks.map((a) => (
                  <span
                    key={a.userId}
                    title={`${a.userName ?? 'Someone'} · ${new Date(a.ackedAt).toLocaleString('en-GB')}${a.isStale ? ' (stale: topic updated since)' : ''}`}
                    className={
                      'inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-semibold border ' +
                      (a.isStale
                        ? 'bg-parchment border-brass text-brass'
                        : 'bg-green text-parchment border-green')
                    }
                  >
                    {a.userAvatar ?? '?'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---- Topic picker modal ---------------------------------------------------

function TopicPickerModal({
  eventId, existingTopicIds, onClose
}: {
  eventId: number
  existingTopicIds: Set<number>
  onClose: () => void
}) {
  const { data: topics, isLoading, error } = useBriefingTopics()
  const add = useAddEventBriefings(eventId)
  const [selected, setSelected] = useState<Set<number>>(new Set())

  function toggle(id: number) {
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function submit() {
    if (selected.size === 0) return
    add.mutate(Array.from(selected), { onSuccess: () => onClose() })
  }

  const available = (topics ?? []).filter((t) => !existingTopicIds.has(t.id))

  return (
    <ModalShell
      title="Add topics to briefing pack"
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); submit() }}
      size="2xl"
      body={
        <div className="space-y-3">
          {isLoading && <p className="text-sm text-ink-soft font-mono">Loading library…</p>}
          {error && <ServerError title="Could not load topics" error={error as Error} />}
          {available.length === 0 && !isLoading && (
            <p className="text-sm italic text-ink-soft">
              No more topics in the library. An admin can add new topics from{' '}
              <span className="font-mono">Admin → Briefing Topics</span>.
            </p>
          )}
          <div className="space-y-1 max-h-[55vh] overflow-y-auto">
            {available.map((t) => {
              const checked = selected.has(t.id)
              return (
                <label
                  key={t.id}
                  className={
                    'flex items-start gap-3 p-3 rounded cursor-pointer transition-colors border ' +
                    (checked ? 'bg-green-soft/40 border-green' : 'bg-white border-line hover:bg-parchment')
                  }
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(t.id)}
                    className="mt-1 accent-green"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold">{t.title}</span>
                      <span className="text-[10px] text-ink-soft uppercase tracking-wide">
                        {t.category}
                      </span>
                      <span className="text-xs text-ink-soft font-mono">~{t.durationMinutes} min</span>
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
          {add.error && <ServerError title="Could not add topics" error={add.error as Error} />}
        </div>
      }
      footer={
        <>
          <CancelButton onClick={onClose} disabled={add.isPending} />
          <PrimaryButton onClick={submit} disabled={selected.size === 0 || add.isPending} type="button">
            {add.isPending ? 'Adding…' : `Add ${selected.size || 0} topic${selected.size === 1 ? '' : 's'}`}
          </PrimaryButton>
        </>
      }
    />
  )
}
